import { ExtendedVariant, Variant } from './Variant';
import { Category } from './Category';
import { RatingDistribution } from '../review/Review';

export type ProductTypeKey =
  | 'cable-railing-bracket'
  | 'cable-railing-cover'
  | 'cable-railing-end-plug'
  | 'cable-railing-express-handrail'
  | 'cable-railing-kit'
  | 'cable-railing-post'
  | 'cable-railing-signature-post'
  | 'side-mount-post'
  | 'slim-side-mount-post'
  | 'glass-panel'
  | 'metal-protector'
  | 'screw'
  | 'cable-railing-stainless-steel-cable'
  | 'cable-railing-tension-kit'
  | 'washer'
  | 'handrail'
  | 'hardware'
  | 'wood-stair-parts-treads'
  | 'wood-handrail-species-dropdown'
  | 'wood-tread-species-dropdown'
  | 'wood-sample-species-dropdown'
  | 'floating-stair';

export type DPQProduct = {
  Item: string;
  Quantity: number;
  Category?: string;
  Description?: string;
  Finish?: string;
  sheen?: string;
  hasWirebrush?: boolean;
};

export const PRODUCTS_WITH_WIREBRUSH: ProductTypeKey[] = ['handrail', 'wood-stair-parts-treads', 'wood-handrail-species-dropdown', 'wood-tread-species-dropdown'];
// Sample products are themselves the sample, so the PDP hides the "Get a sample" link on them.
// FLIGHT stairs (mono, stack, spiral, cantilever). Their lead time is never shown to shoppers — not on
// listings, search, related-product rows or the PDP — nor published in structured data.
export const PRODUCTS_WITHOUT_LEAD_TIME: ProductTypeKey[] = ['floating-stair'];
// An unknown type (e.g. the backend's product-type lookup was unavailable) hides the lead time too:
// better no ship time on an ordinary product than an unverified one on FLIGHT stairs.
export const showsLeadTime = (productType?: string) =>
  !!productType && !PRODUCTS_WITHOUT_LEAD_TIME.includes(productType as ProductTypeKey);
export const SAMPLE_PRODUCTS: ProductTypeKey[] = ['wood-sample-species-dropdown'];
// Samples are wood but carry no wirebrush option, so they belong here and not in PRODUCTS_WITH_WIREBRUSH.
export const WOOD_PRODUCTS: ProductTypeKey[] = ['handrail', 'wood-stair-parts-treads', 'wood-handrail-species-dropdown', 'wood-tread-species-dropdown', 'wood-sample-species-dropdown'];

export interface ReviewRatingStatistics {
  averageRating: number;
  highestRating: number;
  lowestRating: number;
  count: number;
  ratingsDistribution: RatingDistribution;
}

export interface Product {
  productId?: string;
  productKey?: string;
  productRef?: string;
  type?: ProductTypeKey;
  changed?: Date;
  version?: string;
  name?: string;
  slug?: string;
  description?: string;
  categories?: Category[];
  variants: Variant[];
  _url?: string;
  metaTitle?: string;
  metaDescription?: string;
  metaKeywords?: string;
  reviewRatingStatistics?: ReviewRatingStatistics;
  useWirebrush?: boolean;
  isWoodProduct?: boolean;
  /** Maps attribute keys to their localized display labels */
  attributeLabels?: Record<string, string>;
}

export type AttributeOption = { label: string; value: any };

export type VariantDefiningAttribute = {
  key: string;                  // attribute key (e.g. "system-angle")
  name: string;                 // human-friendly label (e.g. "System Angle"), falls back to key
  isColorAttribute: boolean;
  values: AttributeOption[];    // full objects for dropdowns
  primitiveValues?: any[];      // convenience: array of primitive values (value only)
};

export interface ExtendedProductListingProduct extends Product {
  variants: ExtendedVariant[];
  startingAtPrice?: {
    basePrice: string;
    basePriceNumeric: number;
    salePrice?: string;
    salePriceNumeric?: number;
    discountPercentage?: number;
  };
  // Either finishes or colorsMap will be populated — a product uses finish selection (independent of variant)
  // or has a Variant Defining Attribute for color. Each finish contains a display name and an optional hex code.
  finishes?: { name: string; hex: string | null }[];
  // List of available colors across variants for this product, mapped to the first variant SKU with that color.
  colorsMap?: { sku: string; color: string }[];
  variantDefiningAttributes?: VariantDefiningAttribute[];
}

export interface FinishData {
  name: string;
  hex: string;
  multiplier: number;
  price?: number;
}

export interface ExtendedProductDetailProduct extends Product {
  variants: ExtendedVariant[];
  variantDefiningAttributes?: VariantDefiningAttribute[]; // List of Variant Definig Attributes available for this Product
  // Map of variants with all the available options to be selected for each attribute, given the possible combinations and existence of variants
  variantsMap?: {
    [sku: string]: { [attributeKey: string]: string[] };
  };
  // Array of variants with their attributes
  variantDefiningAttributeGroups?: Array<{ variantId: string; sku: string; [key: string]: unknown }>;
  /** Finishes for the "target"/requested variant (backward-compat fallback; includes price) */
  finishes?: {
    [finishKey: string]: FinishData;
  };
  /** Sheens for the "target"/requested variant (backward-compat fallback) */
  sheens?: Array<string>;
  /**
   * Distinct finish option sets shared across variants, keyed by setId. Finishes depend on the
   * finish group (species/material), not the SKU, so a product's variants collapse to a handful
   * of sets. Group-level only: `FinishData.price` is omitted here and materialized per variant on
   * the client (`variant.finishPrice × multiplier`). Look up a variant's set via `variantFinishSetId`.
   */
  finishSets?: {
    [setId: string]: { [finishKey: string]: FinishData };
  };
  /** Maps each variant SKU to its finish set id in `finishSets`. */
  variantFinishSetId?: {
    [sku: string]: string;
  };
  /** Distinct sheen option sets shared across variants, keyed by setId. See `finishSets`. */
  sheenSets?: {
    [setId: string]: string[];
  };
  /** Maps each variant SKU to its sheen set id in `sheenSets`. */
  variantSheenSetId?: {
    [sku: string]: string;
  };
}